import { Component, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, AfterViewInit, inject } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { ActivatedRoute, ParamMap, Params } from "@angular/router";

import { ExpansionPanelComponent, IMenuOption, MenuComponent } from "@tylertech/forge";

import { Command } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { CachedRepositoryService, CacheHelperService, FormMetadataService, KeyHandlerService, NavigationService, SecurityService, StorageService } from "@tylertech/core-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { GuidHelper, Utilities, } from "@tylertech/shared-module";
import { ClassMetadata, IBaseGridViewMetadata, ICombinedViewMetadata, IFormMetadata, IGridViewMetadata, IHeaderBadge, IMetadataAction, ITabbedViewMetadata, IViewMetadata, PropertyMetadata, RecalculateProperty, RepositoryDataCollection, SaveHandlingType } from "@tylertech/tyler-api-types";

import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { AugmentedModelObject, EntityWrapper, goToFormBoundMethodParameter, IAugmentedWithParent, LazyLoadedProxyHandler, MetadataIntermediary, ModifierCommandArgs, onDeleteBoundMethodParameter, onDeleteBoundMethodSignature, RepositoryFormData, } from "../../interfaces/";
import { CellEditService, GridDeleteService, GridEventService, GridMetadataService, MetadataActionService, MetadataErrorService, MetadataEventService, MetadataFormService, MetadataLoadViewService, MetadataPropertyService, MetadataService, MetadataViewService, MultipleEntityEditService, ProviderStorageService } from "../../services";
import { ArrayHelpers, MetadataActionHelpers, ObjectHelpers } from "../../utilities";
import { AllowDeleteFunctionArgs } from "../metadataPanelGrid/metadataPanelGrid.component";
import { RepositoryFunctionArgs } from "../metadataTable/metadataTable/metadata.table.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { takeUntil, Subject } from "rxjs";

export interface PanelSetting
{
    UserId: string;
    TypeName: string;
    Title: string;
    Path: string;
    Open: boolean;
}
export interface CustomMetadataViewBindingObject<T extends IViewMetadata = IViewMetadata>
{
    readonly model: AugmentedModelObject;
    readonly metadata: ClassMetadata;
    readonly focusFirst: boolean;
    readonly view: T;
    readonly datacontroller: RepositoryDataCollection;
    readonly index: number;
    readonly selectedIndex: number;
}
@Component({
    selector: "metadata-view",
    templateUrl: "./metadata.view.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
// REVIEW - We should start considering more specialization in our components.
export class MetadataViewComponent implements OnInit, OnDestroy, OnChanges, DoCheck,
    Record<"onDelete", onDeleteBoundMethodSignature>, AfterViewInit
{
    private readonly $navigationService = inject(NavigationService);
    private readonly $commonDialogService = inject(CommonDialogsService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $metadataActionService = inject(MetadataActionService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataFormService = inject(MetadataFormService);
    private readonly $metadataLoadViewService = inject(MetadataLoadViewService);
    private readonly $metadataErrorService = inject(MetadataErrorService);
    private readonly modelGroup = inject(NgModelGroup);
    private readonly $gridDeleteService = inject(GridDeleteService);
    private readonly $gridMetadataService = inject(GridMetadataService);
    private $metadataEventService = inject(MetadataEventService);
    private $gridEventService = inject(GridEventService);
    private readonly $cacheHelperService = inject(CacheHelperService);
    private $repositoryService = inject(CachedRepositoryService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private $metadataPropertyService = inject(MetadataPropertyService);
    private $providerStorageService = inject(ProviderStorageService);
    private $multipleEditEntityService = inject(MultipleEntityEditService);
    private readonly $formMetadataService = inject(FormMetadataService);
    private $cellEditService = inject(CellEditService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private $securityService = inject(SecurityService);
    private $storageService = inject(StorageService);

    private readonly log: ILogger;

    @Input() viewTitle: string;
    @Input() hideTitle: boolean = false;
    @Input() view: IViewMetadata;
    @Input() datacontroller: RepositoryFormData;
    @Input() model: AugmentedModelObject | AugmentedModelObject[];
    @Input() wrapInForm: boolean = true;
    @Input() isInView: boolean;
    @Input() isFocusFirst: boolean = true;
    @Input() readonly viewOnly: boolean;
    @Input() readonly saveableForm: boolean;
    @Input() index: number;
    @Input() selectedIndex: number;
    @Input() footerView: boolean;
    @Input() onDelete: onDeleteBoundMethodSignature;
    @Output() modifierCallback = new EventEmitter<ModifierCommandArgs>();
    @Input() readonly customViewCommands: ReadonlyArray<Command>;
    @Input() isMenu: boolean;

    @Input() recalculating: boolean;
    @Input() pageHeader: boolean;
    @Input() dialogIndex = 0;
    @Input() parentViewId: string;
    @Input() isStepperView: boolean;
    @Output() recalculatingChange = new EventEmitter<boolean>();

    @ViewChild("expansionPanel") expansionPanel: ExpansionPanelComponent;

    private readonly queryParams = new Map<string, string>();

    gridData: any[] = [];

    viewsArray: IViewMetadata[][];

    tabbedViews: IViewMetadata[];

    rowArray: number[];

    private _loading: boolean = true;
    private _waitingToAdd: boolean;
    private _disableGridAdd: boolean;

    private readonly CollapsiblePanel = "collapsible_panel";
    private _panelOpen: boolean;

    get loading(): boolean
    {
        return this._loading;
    }

    set loading(value: boolean)
    {
        this._loading = value;
        if (this._waitingToAdd && !this._loading)
        {
            if(this.$metadataViewService.IsCellEditGrid(this.view))
            {
                this._disableGridAdd = false;
            }
            this._waitingToAdd = false;
            this.create();
        }
    }

    get disableGridAdd(): boolean
    {
        return this._disableGridAdd;
    }

    gridLoading: boolean;

    doneLoadingViews: boolean;

    initialized: boolean = false;

    private _tabIndex: number = 0;

    @Input() addMode: boolean;

    isCollapsible: boolean;

    menuOptionsLoaded: boolean = false;

    /* Used in the template */ hasErrors: boolean;

    customDataBinding: CustomMetadataViewBindingObject;
    private _menuOptions: IMenuOption[] = [];
    SubMenuItems: string[] = [];
    private editAfterRecalculate: boolean = false;
    private editAfterRecalculateIsAddMode: boolean = false;
    private entityToEditAfterRecalculate: any;
    private cellEditAfterRecalculate: boolean = false;
    private intervalRecalculating: boolean;
    private intervalCanceled: boolean = false;
    private listenerId: string;
    compactMode: boolean = false;
    panelId: string;

    //Makes this view's child ngModelGroup names unique to avoid conflict on view switching
    readonly modelGroupSuffix = Utilities.generateRandomGuid();
    private onIntervalTimeout: ReturnType<typeof setTimeout>;
    private readonly destructor$ = new Subject<void>();

    constructor()
    {
        const activatedRoute = inject(ActivatedRoute);

        this.log = getLoggerFor("MetadataViewComponent",
            () =>
            {
                return this.viewTitle
                    ? `MetadataViewComponent("${this.viewTitle}")`
                    : "MetadataViewComponent";
            });

        activatedRoute.queryParamMap
            .pipe(takeUntilDestroyed())
            .subscribe((paramMap: ParamMap) =>
            {
                this.queryParams.clear();

                paramMap.keys.forEach(
                    (k) =>
                    {
                        this.queryParams.set(k, paramMap.get(k));
                    });
            });
    }

    ngOnInit(): void
    {
        const self = this;
        self.setPanelSetting();
        self.loadGrid();
        self.loadViews();
        self.listenerId = Utilities.generateRandomGuid();
        self.panelId = Utilities.generateRandomHTMLId();

        this.$metadataEventService.onReinitializeView.pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                self.ReInitialize(true);
            });
        if (this.$metadataViewService.IsTabbedView(this.view))
        {
            this.$metadataEventService.onNavigateTabbedView.pipe(takeUntil(this.destructor$))
                .subscribe(viewTitle =>
                {
                    const index = self.visibleTabViews.findIndex(x => x.Title === viewTitle);
                    if (index > -1)
                    {
                        self.activateTabImpl(index);
                    }
                });
        }

        if (Utilities.IsNullOrUndefined(self.view["$InitialViewOnly"]))
            self.view["$InitialViewOnly"] = self.view.ViewOnly;
        self.view.ViewOnly = self.view["$InitialViewOnly"];
        if (self.isViewOnly)
        {
            self.view.ViewOnly = true;
        }
        if (self.view.ViewOnlyProperty)
        {
            self.$gridEventService.onRefreshGrid.pipe(takeUntil(this.destructor$))
                .subscribe(() =>
                {
                    if (self.view.TitleProperty)
                    {
                        self.setTitleFromTitleProperty();
                    }
                    self.setViewOnlyFromProperty();
                });
        }
        self.$metadataEventService.onRecalculateWorking.pipe(takeUntil(this.destructor$))
            .subscribe(args =>
            {
                if (self.view.TitleProperty && !args.Recalculating) self.setTitleFromTitleProperty();

                self.loadHeaderBadges();

                if (self.view.ViewOnlyProperty && !args.Recalculating)
                    self.setViewOnlyFromProperty();
                self.recalculating = args.Recalculating;
                self.recalculatingChange.emit(self.recalculating);

                if (self.isCollapsible && self.view.CollapsedProperty)
                {
                    self.panelOpen = !self.evaluateCollapsedProperty();
                    requestAnimationFrame(() => self.expansionPanel.open = self.panelOpen);
                }
            });
        this.$metadataEventService.onSaveAfterRecalculate.pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                if (self.editAfterRecalculate)
                {
                    self.editAfterRecalculate = false;
                    self.gridLoading = false;
                    self.editGrid(self.entityToEditAfterRecalculate, self.editAfterRecalculateIsAddMode);
                }
                else if (self.cellEditAfterRecalculate)
                {
                    self.cellEditAfterRecalculate = false;
                    self.fireCellEditOnAdd(self.entityToEditAfterRecalculate);
                }
            });
        this.registerCacheCleaner();
        this.compactMode = this.$securityService.IsCompactMode;
    }

    ngAfterViewInit()
    {
        if (this.$metadataViewService.IsCombinedView(this.view))
        {
            const self = this;
            const rows = this.rowArray;

            rows.forEach(row =>
            {
                const views = this.getViewsForRow(row);
                views.forEach(view =>
                {
                    if (self.$metadataViewService.IsGrid(view))
                    {
                        view["$combinedViewTable"] = true;
                        if (row === this.viewsArray.length - 1)
                        {
                            if (Utilities.IsNullOrUndefined(view.DrawWidth) || view.DrawWidth === 100)
                            {
                                view["$fixedHeight"] = !this.saveableForm;
                            }
                        }
                    }
                });
            });
        }

        this.initializeAddEditNew();
    }

    ngOnDestroy()
    {
        clearTimeout(this.onIntervalTimeout);
        this.intervalCanceled = true;
        this.recalculating = false;
        this.recalculatingChange.emit(false);
        this.$keyHandlerService.unsubscribeAllFromClass(this.listenerId);
        this.destructor$.next();
        this.destructor$.complete();
    }

    get hasCustomCommands(): boolean { return this.customViewCommands?.length > 0; }

    registerKeyboardShortcut(disabled: boolean, property: PropertyMetadata): void
    {
        if (disabled)
        {
            if (property.ShortcutMetadata)
                this.$keyHandlerService.unsubscribe(property.ShortcutMetadata.Key, property.ShortcutMetadata.ModifierKey);
        }

        else
            this.$keyHandlerService.registerShortcutMetadata(this.listenerId, property.ShortcutMetadata, `This Screen|${property.Label}`, () => this.menuItemSelectedImpl(property.Name));
    }

    registerCacheCleaner(): void
    {
        if (!this.$metadataViewService.IsGrid(this.view))
            return;
        const self = this;

        this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntil(this.destructor$))
            .subscribe((type) =>
            {
                if (type === self.getGridTypeName())
                    self.loadGrid();
            });
    }

    setViewOnlyFromProperty(): void
    {
        this.view.ViewOnly =
            this.viewOnly ||
            (!Utilities.IsNullOrUndefinedOrEmpty(this.view.ViewOnlyProperty) &&
                this.EvaluateBoolean(this.view.ViewOnlyProperty));
    }

    ReInitialize(fromRecalculate: boolean): Promise<any>
    {
        return new Promise<any>((resolve, _reject) =>
        {
            if (this.$metadataViewService.IsCombinedView(this.view) && this.viewsArray[0].length === 0)
            {
                this.loadViews();
            }

            if (this.initialized && this.hasCollapsibleMenu())
                this.loadMenuOptions();

            if (!this.$metadataViewService.IsGrid(this.view))
            {
                resolve(undefined);
                return;
            }
            else if (this.view.HiddenProperty)
            {
                let model = this.model;
                if (this.view.SubPropertyType && this.datacontroller.OtherEntities[this.view.SubPropertyType])
                {
                    model = this.datacontroller.OtherEntities[this.view.SubPropertyType][0];
                }
                if (this.$metadataService.GetPermissionDenied(false, this.view.HiddenProperty, model))
                {
                    resolve(undefined);
                    return;
                }
            }

            if (Utilities.IsNullOrUndefined(this.view.GridProperties["$InitialCanAdd"]))
                this.view.GridProperties["$InitialCanAdd"] = this.view.GridProperties.CanAdd;
            if (this.view.CanAddProperty)
            {
                this.view.GridProperties.CanAdd = this.getPermission(
                    this.view.GridProperties["$InitialCanAdd"],
                    this.view.CanAddProperty
                );
            }
            if (Utilities.IsNullOrUndefined(this.view.GridProperties["$InitialCanEdit"]))
                this.view.GridProperties["$InitialCanEdit"] = this.view.GridProperties.CanEdit;
            if (this.view.CanEditProperty || this.viewOnly)
            {
                this.view.GridProperties.CanEdit = this.getPermission(
                    this.view.GridProperties["$InitialCanEdit"],
                    this.view.CanEditProperty
                );
            }
            else
            {
                this.view.GridProperties.CanEdit = this.view.GridProperties["$InitialCanEdit"];
            }
            if (Utilities.IsNullOrUndefined(this.view.GridProperties["$InitialCanDelete"]))
                this.view.GridProperties["$InitialCanDelete"] = this.view.GridProperties.CanDelete;
            if (this.view.CanDeleteProperty || this.viewOnly)
            {
                this.view.GridProperties.CanDelete = this.getPermission(
                    this.view.GridProperties["$InitialCanDelete"],
                    this.view.CanDeleteProperty
                );
            }
            else
            {
                this.view.GridProperties.CanDelete = this.view.GridProperties["$InitialCanDelete"];
            }
            if (
                !Utilities.IsNullOrUndefined(this.gridData) &&
                this.gridData.length === 0 &&
                this.view.GridProperties.CanAdd &&
                (Utilities.IsNullOrUndefined(this.isInView) || this.isInView) &&
                this.view.EnableAutomaticEntry && !this.view["$AutoEntry"] && !fromRecalculate
            )
            {
                this.view["$AutoEntry"] = true;
                if (this.loading)
                {
                    this._waitingToAdd = true;
                    this._disableGridAdd = true;
                    resolve(undefined);
                }
                else
                {
                    resolve(this.create());
                }
            }
            else
            {
                resolve(undefined);
            }
        });
    }

    getAddPermission(): boolean
    {
        const self = this;
        if (!self.$metadataViewService.IsGrid(self.view))
        {
            return false;
        }
        if (Utilities.IsNullOrUndefined(self.view.GridProperties["$InitialCanAdd"]))
        {
            self.view.GridProperties["$InitialCanAdd"] = self.view.GridProperties.CanAdd;
        }
        return self.getPermission(self.view.GridProperties["$InitialCanAdd"], self.view.CanAddProperty);
    }

    clearCacheAndLoadGrid(): void
    {
        const type = this.view["GridProperties"].ClassMetadata.TypeName;
        if (!this.projectionOrServerGrid) this.datacontroller.Clear(type);
        this.$cacheHelperService.clearAllForType(type);
        this.loadGrid();
    }

    loadGrid(): Promise<any>
    {
        const self = this;
        if (
            this.$metadataViewService.IsProjectionGrid(this.view) &&
            this.view?.EditForm?.SaveHandling === SaveHandlingType.Immediate
        )
        {
            const type = this.view["RowTypeName"] ?? this.view.GridProperties["RowTypeName"];
            this.datacontroller.Clear(type);
            this.$cacheHelperService.clearAllForType(type);
        }
        if (this.serverPaging)
        {
            if (
                this.view["EditForm"] &&
                this.$metadataLoadViewService.recalculating[this.view["EditForm"].BoundType]
            )
            {
                self.$metadataEventService.fireRecalculateWorkingListeners(
                    {
                        ShowLoading: false,
                        Recalculating: true,
                    },
                    true
                );
                return this.$repositoryService
                    .RecalculateGridView({
                        Item1: this.datacontroller,
                        Item2: self.view["GridId"],
                    })
                    .then(
                        rdc =>
                        {
                            self.$metadataActionService.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc);
                            self.datacontroller.Merge(rdc);
                            delete self.$metadataLoadViewService.recalculating[self.view["EditForm"].BoundType];
                            self.$metadataService.FireGridReload(rdc.Type, true);
                            self.$metadataEventService.fireRecalculateWorkingListeners(
                                {
                                    ShowLoading: false,
                                    Recalculating: false,
                                },
                                true
                            );
                            finalizeServerLoad();
                        },
                        self.$metadataActionService.GetVerificationHandler(
                            self.datacontroller,
                            () => self.loadGrid(),
                            self.model
                        )
                    );
            }
            else
            {
                return Promise.resolve(finalizeServerLoad());
            }
        }
        else
        {
            return loadLocal();
        }

        function loadLocal(): Promise<any>
        {
            self.gridLoading = true;
            return self.$metadataLoadViewService
                .LoadView(self.model, self.view, self.datacontroller)
                .then(loadGridItems)
                .then(_r =>
                {
                    if (self.$metadataViewService.IsProjectionGrid(self.view))
                        setFilters();
                    self.finishLoading();
                    self.gridLoading = false;
                    self.initialized = true;
                });
        }

        function setFilters(): void
        {
            const pagingFilter = self.$metadataViewService.createFilterString(self.model, self.view);
            if (!pagingFilter)
                return;
            if (self.view["$$OriginalConstantFilter"] === undefined)
                self.view["$$OriginalConstantFilter"] = self.view.ConstantFilter ?? null;
            self.view.ConstantFilter = pagingFilter.replace("&$filter=", "");
        }

        function finalizeServerLoad(): void
        {
            setFilters();
            self.gridData = undefined;
            self.finishLoading();
            self.gridLoading = false;
            if (self.initialized)
            {
                // need to fire event for infoTable to fetch the data
                self.$gridEventService.fireTableEntitiesChangedListeners(
                    self.view["GridProperties"]["$providerId"]
                );
            }
            else
            {
                // data will be loaded on init
                self.initialized = true;
            }
        }

        function loadGridItems(gridItems: any[]): unknown
        {
            if (!gridItems)
            {
                return;
            }
            if (self.$metadataViewService.IsGrid(self.view) || self.$metadataViewService.IsCardList(self.view))
            {
                self.gridData = gridItems;
                return self.ReInitialize(false);
            }
            if (self.$metadataViewService.IsOneToOneView(self.view))
            {
                self.model = gridItems[0];
            }
        }
    }

    fireTableListeners(): void
    {
        if (this.$metadataViewService.IsGrid(this.view))
        {
            this.$metadataService.FireGridReload(this.view.GridProperties.ClassMetadata.TypeName, false);
        }
    }

    get ShowMaxRowMessage(): boolean
    {
        return (
            this.$metadataViewService.IsTable(this.view) &&
            this.view["GridProperties"].MaxRowLimit &&
            this.gridData.length === 50000
        );
    }

    get MaxRowMessage(): string
    {
        if (!this.view["GridProperties"].MaxRowMessage) return "Showing the first 50,000 results";
        return this.$metadataService.EvaluateString(this.view["GridProperties"].MaxRowMessage, null, this.model);
    }

    loadViews(): void
    {
        let currentRow = 0;
        let currentPercent = 0;
        this.viewsArray = [];
        this.rowArray = [currentRow];
        this.viewsArray[currentRow] = [];
        if (this.IsCombinedView)
        {
            if (this.IsTabbedView)
            {
                this.setTabbedViews();
            }
            else
            {
                const combinedView = <ICombinedViewMetadata>this.view;
                for (let index = 0; index < combinedView.Views.length; index++)
                {
                    const view = combinedView.Views[index];
                    if (Utilities.IsNullOrUndefined(view.DrawWidth))
                    {
                        view.DrawWidth = 100;
                    }
                    if (currentPercent + view.DrawWidth > 100)
                    {
                        currentRow++;
                        this.viewsArray[currentRow] = [];
                        this.rowArray.push(currentRow);
                        currentPercent = 0;
                    }
                    //push combined view row
                    view["$$ParentView"] = this.view;
                    this.viewsArray[currentRow].push(view);
                    currentPercent += view.DrawWidth;
                }
            }
        }
        this.isCollapsible = this.view.Collapsible || this.view.DefaultCollapsed;
        this._panelOpen = this.initialPanelOpen();
        this.doneLoadingViews = true;

        const navListForm = document.querySelector(".nav-list-form");
        if (navListForm)
        {
            navListForm.scrollTop = 0;
        }

        this.loadHeaderBadges();
    }

    initialPanelOpen(): boolean
    {
        let ret = true;

        if (!Utilities.IsNullOrUndefined(this.panelOpen))
        {
            ret = this.panelOpen;
        }
        else if (this.view.CollapsedProperty)
        {
            ret = !this.evaluateCollapsedProperty();
        }
        else if (!Utilities.IsNullOrUndefined(this.view.DefaultCollapsed))
        {
            ret = !this.view.DefaultCollapsed;
        }
        return ret;
    }

    evaluateCollapsedProperty(): boolean
    {
        return this.$metadataService.EvaluateBoolean(this.view.CollapsedProperty, null, this.model);
    }

    get panelOpen(): boolean
    {
        if(this.view.Collapsible && this.view.CollapsedProperty)
        {
            return !this.evaluateCollapsedProperty();
        }
        return this._panelOpen;
    }

    set panelOpen(value: boolean)
    {
        this._panelOpen = value;
        if(!this.view.CollapsedProperty)
        {
            this.savePanelSetting(value);
        }
    }

    get isPanelOpen(): boolean
    {
        return this.panelOpen;
    }

    menuOpened(e): void
    {
        e.target.activateFirstOption();
    }

    menuItemSelected(e): void
    {
        this.menuItemSelectedImpl(e.detail.value);
    }

    menuItemSelectedImpl(property: string): void
    {
        const self = this;
        let prop: MetadataIntermediary;
        this.$metadataLoadViewService.LoadView(this.model, this.view.CollapsibleMenu, this.datacontroller)
            .then(result =>
            {
                let model = result ? result[0] : self.model;
                prop = this.$metadataService.FlattenProperties(self.view.CollapsibleMenu.ViewMetaData, model.$$Parent, model).filter(x => x.property.Name === property)[0];
                const shouldRecalculate = !Utilities.IsNullOrUndefined(prop.property.RecalculateOnChange);

                if (self.SubMenuItems.indexOf(prop.property.Name) < 0)
                    model = prop.model;

                let type = model["$$TypeName"];

                if (!type && self.datacontroller)
                    type = self.datacontroller.Type;
                if (self.view.SubPropertyType && self.datacontroller.OtherEntities[self.view.SubPropertyType])
                    type = self.view.SubPropertyType;
                if (self.view.SubPropertyName)
                    type = self.view.CollapsibleMenu.ViewMetaData.TypeName;

                if (prop.property.ActionMetadata)
                {
                    if (MetadataActionHelpers.isRequiredValid(prop.property.ActionMetadata))
                    {
                        const errors = this.$metadataErrorService.touchAndGetFormErrors(this.modelGroup.control.controls, [this.view]);
                        if (errors.length > 0)
                        {
                            this.$metadataErrorService.showVerificationAlertDialog(errors);
                            return;
                        }
                    }
                    let controller = this.datacontroller;
                    if (!prop.property.ActionMetadata.ServicePath)
                        controller = null;
                    if (this.viewOnly)
                        prop.property.ActionMetadata.ViewOnly = true;
                    else if (!Utilities.IsNullOrUndefined(prop.property.ActionMetadata.ViewToShow))
                        setFocusOnLoad(prop.property.ActionMetadata.ViewToShow);
                    this.$metadataActionService.ExecuteAction(model, prop.property.ActionMetadata, controller)
                        .then((updatedModel: any) =>
                        {
                            if (!Utilities.IsNullOrUndefined(updatedModel) && Utilities.IsNullOrUndefined(updatedModel["canceledActionDialog"]) && shouldRecalculate)
                                doRecalculate(type, updatedModel);
                        });
                }
                else if (shouldRecalculate)
                    doRecalculate(type, model);
            });

        function doRecalculate(type: any, model: any): void
        {
            self.$metadataPropertyService.recalculateProperties(
                type,
                prop.property,
                model,
                model["$$Parent"],
                model[prop.property.Name],
                undefined,
                true,
                true,
                self.datacontroller
            );
        }

        function setFocusOnLoad(view: IViewMetadata): void
        {
            if (self.$metadataViewService.IsCombinedView(view))
                setFocusOnLoad(view["Views"][0]);
            else if (view.ViewMetaData?.Properties.length > 0)
                view.ViewMetaData.Properties[0].FocusOnLoad = true;
        }
    }

    get showHeaderBadge(): boolean
    {
        return !!this.visibleHeaderBadges && this.visibleHeaderBadges.length > 0;
    }

    private _headerBadges: IHeaderBadge[] = null;
    get headerBadges(): IHeaderBadge[]
    {
        return this._headerBadges;
    }

    set headerBadges(value: IHeaderBadge[])
    {
        this._headerBadges = value;
    }

    get visibleHeaderBadges(): IHeaderBadge[]
    {
        return this.headerBadges?.filter(
            x => !this.$metadataService.EvaluateBoolean(x.HiddenProperty, null, this.model)
        );
    }

    get menuOptions(): IMenuOption[]
    {
        return this._menuOptions;
    }

    get showMenu(): boolean
    {
        return this.hasCollapsibleMenu() && this.menuOptionsLoaded && this.menuOptions?.length > 0;
    }

    hasCollapsibleMenu(): boolean
    {
        if (this.view.CollapsibleMenu && !this.isHidden(this.view.CollapsibleMenu))
            return true;

        return false;
    }

    getViewsForRow(index: number): IViewMetadata[]
    {
        if (Utilities.IsNullOrUndefined(this.viewsArray))
        {
            this.viewsArray = [];
        }
        if (Utilities.IsNullOrUndefined(this.viewsArray[index]))
        {
            this.viewsArray[index] = [];
        }
        return this.viewsArray[index];
    }

    firstRowAndColumn(i: IViewMetadata): boolean
    {
        return i === this.getViewsForRow(0)[0];
    }

    viewHasTable(view: IViewMetadata): string
    {
        return this.$metadataViewService.IsGrid(view) ? "combined-view--table-view" : "";
    }

    fillSpaceClassForTabView(views: IViewMetadata[]): string
    {
        if (!Utilities.IsNullOrUndefined(views) && views.length === 1)
        {
            return "stretch-table-view";
        }
    }

    get tabIndex(): number
    {
        return this._tabIndex;
    }

    set tabIndex(index: number)
    {
        this._tabIndex = index;
    }

    activateTab(event: any): void
    {
        this.activateTabImpl(event.detail.index);
    }

    activateTabImpl(index: number): void
    {
        const tabView: IViewMetadata = this.visibleTabViews[index];
        this.tabbedViews.forEach(x => x["$currentIndex"] = undefined);
        tabView["$currentIndex"] = index;
        this.tabIndex = index;
        this.evaluateOnShownRecalc(tabView);

        //TODO Get specific grid provider id to resize
        this.$gridEventService.fireGridResizeListener("");
    }

    evaluateOnShownRecalc(view: IViewMetadata): void
    {
        const self = this;
        if (this.$metadataViewService.IsCombinedView(view))
            view.Views.forEach(v =>
            {
                self.evaluateOnShownRecalc(v);
            });
        else if (view.OnShown) this.runRecalcProperty(view.OnShown)
            .then(_r => (this.loading = false));
    }

    trackByTitle(_index: number, item: IViewMetadata): string
    {
        return item.Title;
    }

    get visibleTabViews(): IViewMetadata[]
    {
        if (!this.tabbedViews)
            return [];
        this.tabbedViews.forEach(x =>
        {
            if (!x["$$ParentView"])
                x["$$ParentView"] = this.view;
        });
        return this.tabbedViews.filter(view => !this.isHidden(view));
    }

    private setTabbedViewGrids(view: IViewMetadata): void
    {
        if (this.$metadataViewService.IsCombinedView(view))
        {
            view.Views.forEach(childView => this.setTabbedViewGrids(childView));
        }
        else
        {
            if (this.$metadataViewService.IsGrid(view))
            {
                view["$tabbedViewGrid"] = true;
            }
        }
    }

    private setTabbedViews(): void
    {
        const self = this;
        const tabbedView = <ITabbedViewMetadata>this.view;

        tabbedView.Views.forEach(view =>
        {
            this.setTabbedViewGrids(view);
        });

        if (tabbedView.GroupBy === "None")
        {
            this.tabbedViews = tabbedView.Views;
        }
        const view = tabbedView.Views[0];
        if (this.$metadataViewService.IsGrid(view) && tabbedView.Views.length <= 1)
        {
            let subProp = view.SubPropertyName;
            this.$metadataLoadViewService.LoadView(this.model, view, this.datacontroller)
                .then(items =>
                {
                    if (view.SubPropertyType && Utilities.IsNullOrUndefined(view.SubPropertyName))
                    {
                        const filter = self.$metadataService.CreateLoadFilter(
                            self.model,
                            view.ChildColumnName,
                            view.ParentColumnName,
                            view.ConstantFilter
                        );
                        self.datacontroller.GetOrFetch(self.$repositoryService, view.SubPropertyType, filter)
                            .then(x =>
                            {
                                return setTabbedViewData(x[0]);
                            });
                    }
                    else
                    {
                        return setTabbedViewData(self.model);
                    }
                    function setTabbedViewData(model: AugmentedModelObject | AugmentedModelObject[]): any
                    {
                        let data: Array<AugmentedModelObject> = items;
                        if (Utilities.IsNullOrUndefined(subProp))
                        {
                            subProp = "TabbedView";
                        }
                        else
                        {
                            data = model[subProp];
                        }
                        const groupByField = tabbedView.GroupBy;
                        const groups = data.reduce((agg, x) =>
                        {
                            const groupValue = groupByField in x ? x[groupByField] : undefined;
                            if (!(groupValue in agg))
                            {
                                agg[groupValue] = [];
                            }
                            agg[groupValue].push(x);
                            return agg;
                        }, {});
                        self.tabbedViews = Object.keys(groups)
                            .map((groupName, i) =>
                            {
                                // IViewMetadata's shouldn't have any circular references or non-serialize-able properties, this should be good enough to clone.
                                const v = JSON.parse(JSON.stringify(view));
                                const newSubProp = `$$${subProp}${i}`;
                                model[newSubProp] = data.filter(x => x[tabbedView.GroupBy] === groupName);
                                v.SubPropertyName = newSubProp;
                                v.Title = `${groupName} (${model[newSubProp].length})`;
                                return v;
                            });
                    }
                });
        }
        else
        {
            this.tabbedViews = tabbedView.Views;
        }
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        if (!this.initialized)
        {
            return;
        }
        const self = this;
        if (changes.isInView && changes.isInView.currentValue)
        {
            this.loading = true;
            this.ReInitialize(false)
                .then(_r =>
                {
                    self.finishLoading();
                });
        }
    }

    finishLoading(): void
    {
        const self = this;
        if (this.view.TitleProperty)
        {
            this.setTitleFromTitleProperty();
        }

        if (this.$multipleEditEntityService.DoCopy && this.$multipleEditEntityService.CopyProperties)
        {
            this.$multipleEditEntityService.DoCopy = false;
            const dataController = this.$multipleEditEntityService.DataController;
            this.$multipleEditEntityService.CopyProperties.forEach(copyProperty =>
            {
                if (!copyProperty.CanCopyProperty || this.$metadataService.EvaluateBoolean(copyProperty.CanCopyProperty, dataController.Entity.$$Parent, dataController.Entity, dataController))
                {
                    if (copyProperty.EntityTypeName)
                    {
                        //in the case of multiple views loading, keep checking  until the view that populates OtherEntities has loaded
                        if (!this.datacontroller.OtherEntities[copyProperty.EntityTypeName])
                            this.$multipleEditEntityService.DoCopy = true;
                        else
                            this.datacontroller.OtherEntities[copyProperty.EntityTypeName][0][copyProperty.PropertyName] = dataController.OtherEntities[copyProperty.EntityTypeName][0][copyProperty.PropertyName];

                    }
                    else
                        this.$metadataService.SetModuleValue(copyProperty.PropertyName, null, this.model, this.$metadataService.DependentModuleValue(copyProperty.PropertyName, null, dataController.Entity));
                }
            });
        }

        if (this.view.OnShown && !this.view["$lazyLoadComplete"] && (!this.view["$$ParentView"] || !this.$metadataViewService.IsDescendantOfTabbedView(this.view) || this.$metadataViewService.InitialViewOfTabbedView(this.view)))
        {

            if (this.childFormIsMultipleEditing && this.$metadataViewService.IsGrid(this.view))
                this.$multipleEditEntityService.setFilteredOrder(this.view, this.$providerStorageService.LocalEntitiesStore[this.view.GridProperties["$providerId"]]);

            this.runRecalcProperty(this.view.OnShown, this.$metadataViewService.IsCombinedView(this.view), true)
                .then(_r => finalize());
        }
        else
        {
            delete this.view["$lazyLoadComplete"];
            finalize();
        }

        this.datacontroller.InitializeFormData();
        this.loadMenuOptions();
        if (this.view.OnInterval)
            this.setupReloadInterval();

        this.customDataBinding = {
            model: this.model,
            metadata: this.view.ViewMetaData,
            focusFirst: this.isFocusFirst,
            view: this.view,
            datacontroller: this.datacontroller,
            index: this.index,
            selectedIndex: this.selectedIndex,
        };

        function finalize(): void
        {
            self.loading = false;
            setTimeout(() =>
            {
                if (!self.$metadataViewService.IsCombinedView(self.view))
                {
                    if (!self.view["$viewId"])
                        self.view["$viewId"] = self.listenerId;
                    self.$metadataEventService.fireLoadViewCompletedListener(self.view["$viewId"]);
                }
            }, 0);
        }
    }

    setTitleFromTitleProperty(): void
    {
        const self = this;
        const title = self.$metadataViewService.GetTitle(self.view, self.model, self.datacontroller);
        self.viewTitle = title;
        self.view.Title = title;
    }

    loadHeaderBadges(): void
    {
        if (this.view.HeaderBadges)
        {
            this.headerBadges = [];
            this.view.HeaderBadges.forEach(badge =>
            {
                const text = this.$metadataService.EvaluateString(
                    badge.Text, null, this.model
                );

                this.headerBadges.push({
                    Theme: badge.Theme,
                    Text: text,
                    HiddenProperty: badge.HiddenProperty
                });
            });
        }
        else
        {
            this.headerBadges = null;
        }
    }

    get childFormIsMultipleEditing(): boolean
    {
        return this.datacontroller["$$childFormIsEditing"];
    }

    loadMenuOptions(): void
    {
        const self = this;
        if (this.hasCollapsibleMenu())
        {
            this.menuOptionsLoaded = false;
            this._menuOptions = [];
            this.SubMenuItems = [];

            this.$metadataLoadViewService.LoadView(this.model, this.view.CollapsibleMenu, this.datacontroller)
                .then(result =>
                {
                    const model = result ? result[0] : self.model;
                    const properties = this.$metadataService
                        .FlattenProperties(self.view.CollapsibleMenu.ViewMetaData, model.$$Parent, model)
                        .filter(
                            x =>
                                !this.$metadataService.IsHiddenProperty(x.property) &&
                                !this.$metadataService.EvaluateBoolean(x.property.HiddenProperty, model.$$Parent, model)
                        )
                        .sort((a, b) => a.property.RenderPosition - b.property.RenderPosition);
                    AddSubMenuItems(properties);
                    properties.forEach(property =>
                    {
                        let disabled = self.$metadataPropertyService.isDisabled(property.property, model);
                        const menuOption = <IMenuOption>{
                            value: property.property.Name,
                            label: self.$metadataPropertyService.getLabel(property.property, model),
                            disabled: disabled
                        };
                        self.registerKeyboardShortcut(disabled, property.property);
                        if (property.property.InputType === "Header" && property.property.ClassMetadata)
                        {
                            const subMenuItems: IMenuOption[] = [];
                            const subItems = property.property.ClassMetadata.Properties.filter(x =>
                                !this.$metadataService.IsHiddenProperty(x) &&
                                !this.$metadataService.EvaluateBoolean(x.HiddenProperty, model.$$Parent, model)
                            )
                                .sort((a, b) => a.RenderPosition - b.RenderPosition);

                            subItems.forEach((x) =>
                            {
                                disabled = self.$metadataPropertyService.isDisabled(x, model);
                                subMenuItems.push({
                                    value: x.Name,
                                    label: self.$metadataPropertyService.getLabel(x, model),
                                    disabled: disabled
                                });
                                self.registerKeyboardShortcut(disabled, x);
                            });
                            menuOption.options = subMenuItems;
                        }

                        if (self.SubMenuItems.indexOf(property.property.Name) < 0)
                            self._menuOptions.push(menuOption);
                    });
                })
                .finally(() => self.menuOptionsLoaded = true);
        }

        function AddSubMenuItems(properties: MetadataIntermediary[]): void
        {
            const headers = self.view.CollapsibleMenu.ViewMetaData.Properties.filter(x => x.InputType === "Header");
            if (headers && headers.length > 0)
            {
                properties.forEach(i =>
                {
                    headers.forEach(x =>
                    {
                        if (x.ClassMetadata && x.ClassMetadata.Properties.filter(p => p.Name === i.property.Name).length > 0)
                            self.SubMenuItems.push(i.property.Name);
                    });
                });
            }
        }
    }

    runRecalcProperty(recalcProperty: RecalculateProperty, showLoading: boolean = false, onShown: boolean = false): Promise<any>
    {
        const PropertyMetadata = <PropertyMetadata>{
            Name: recalcProperty.PropertyToChange,
            RecalculateOnChange: this.$metadataPropertyService.getPropertiesToRecalculate(recalcProperty, true),
            RecalculateWithParent: recalcProperty.RecalculateWithParent,
            RecalculateDoNotSend: recalcProperty.PropertiesToExcludeInSend,
            RecalculateToIgnore: recalcProperty.PropertiesToIgnore
        };
        const model =
            this.view.SubPropertyType && this.datacontroller.OtherEntities[this.view.SubPropertyType]
                ? this.datacontroller.OtherEntities[this.view.SubPropertyType][0]
                : this.model;
        const typeName = model["$$TypeName"] ? model["$$TypeName"] : this.datacontroller.Type;
        if (onShown)
            this.datacontroller["$setInitialModCounter"] = Utilities.IsNullOrUndefined(this.datacontroller["$setInitialModCounter"]) ? 1 : this.datacontroller["$setInitialModCounter"] + 1;
        return this.$metadataPropertyService.recalculateProperties(
            typeName,
            PropertyMetadata,
            model,
            model["$$Parent"],
            model[recalcProperty.PropertyToChange],
            undefined,
            false,
            showLoading,
            this.datacontroller
        );
    }

    ngDoCheck(): void
    {
        const self = this;
        this.hasErrors =
            !this.$metadataViewService.IsCombinedView(this.view) &&
            !this.$metadataViewService.IsCard(this.view) &&
            doesViewHaveErrors(this.view);
        if (this.intervalRecalculating && !this.doReload)
            this.intervalRecalculating = false;
        function doesViewHaveErrors(view: IViewMetadata): boolean
        {
            const isGrid = self.$metadataViewService.IsGrid(view);
            if (!isGrid)
            {
                return self.$metadataErrorService.viewHasErrors(view, self.model);
            }
            else if (!self.$metadataViewService.IsTable(self.view))
            {
                const data = self.gridData;
                return (
                    !!data &&
                    (Array.isArray(data)
                        ? ArrayHelpers.any(data, x => self.$metadataErrorService.hasErrors(x))
                        : self.$metadataErrorService.hasErrors(data))
                );
            }
        }
    }

    get hasActions(): boolean
    {
        return (
            this.viewActions &&
            this.viewActions.filter(
                x => !this.$metadataService.GetPermissionDenied(x.Hidden, x.HiddenProperty, this.model, this.datacontroller)
            ).length > 0
        );
    }

    get viewActions(): IMetadataAction[]
    {
        return this.view.Actions;
    }

    /** returns the minimum number of rows to show. */
    get GridMinimum(): number
    {
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            return -1;
        }
        return !this.view.GridProperties.MinCount ? -1 : this.view.GridProperties.MinCount;
    }

    /**
     * TODO - I have no idea what the purpose of the two `<input type="number" hidden>` elements in the template are doing with this.
     */
    get numberOfRowsToShow(): number
    {
        return this.gridData ? this.gridData.length : 0;
    }

    set numberOfRowsToShow(v: number)
    {
        if (!!this.gridData && v >= 0) this.gridData.length = v;
    }

    get IsCustom(): boolean
    {
        return this.$metadataViewService.IsCustom(this.view);
    }

    /**
     * @deprecated Just call the corresponding method on $metadataViewService. It supports more advanced typing capabilities.
     */
    get IsCombinedView(): boolean
    {
        return this.$metadataViewService.IsCombinedView(this.view);
    }

    /**
     * @deprecated Just call the corresponding method on $metadataViewService. It supports more advanced typing capabilities.
     */
    get IsTabbedView(): boolean
    {
        return this.$metadataViewService.IsTabbedView(this.view);
    }

    /**
     * @deprecated Just call the corresponding method on $metadataViewService. It supports more advanced typing capabilities.
     */
    get IsCard(): boolean
    {
        return this.$metadataViewService.IsCard(this.view);
    }

    get directSave(): boolean
    {
        //TODO: this should maybe be expanded for more options, probably derived from gridmetadata to allow immediate save or not
        return this.serverPaging && !this.projectionOrServerGrid;
    }

    get IsSelfContained(): boolean
    {
        return (
            this.$metadataViewService.IsCustom(this.view) &&
            !Utilities.IsNullOrUndefined(this.view.IsSelfContained) &&
            this.view.IsSelfContained
        );
    }

    get IsGrid(): boolean
    {
        return this.$metadataViewService.IsGrid(this.view);
    }

    get IsMetadataPanelGrid(): boolean
    {
        return this.$metadataViewService.IsMetadataPanelGrid(this.view);
    }

    get IsCompact(): boolean
    {
        return this.$metadataViewService.IsCompactMetadataPanelgrid(this.view);
    }

    get IsTable(): boolean
    {
        return this.$metadataViewService.IsTable(this.view);
    }

    get IsCardList(): boolean
    {
        return this.$metadataViewService.IsCardList(this.view);
    }

    get IsTree(): boolean
    {
        return this.$metadataViewService.IsTree(this.view);
    }

    wrapWithCard(view: IViewMetadata): boolean
    {
        return !this.isSingleCardView(view) ? !(this.$metadataViewService.IsGrid(view) || this.$metadataViewService.IsCard(view)) || this.$metadataViewService.IsCardList(view) : false;
    }

    HasTitle(): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(this.viewTitle) && !this.hideTitle;
    }

    get displayGridHeader(): boolean
    {
        if (this.IsGrid)
        {
            return !this.hideTitle && this.view.Title !== this.viewTitle;
        }

        return false;
    }

    isPageHeader(): boolean
    {
        if (!Utilities.IsNullOrUndefined(this.pageHeader))
        {
            return this.pageHeader;
        }

        if (this.$metadataViewService.IsCombinedView(this.view) && !this.view["$$ParentView"]?.Views)
        {
            return this.$metadataViewService.HideCombinedViewTitle(this.view) ? this.view.Title === this.view.Views[0].Title : true;
        }
        else
        {
            if (this.view["$$ParentView"]?.Views.length > 0 && this.view["$$ParentView"].Title !== "")
            {
                return this.view["$$ParentView"].Title === this.view.Title;
            }
        }
        return !this.hideTitle;
    }


    createFilterString(): string
    {
        return this.$metadataService.CreateLoadFilter(
            this.model,
            this.view.ChildColumnName,
            this.view.ParentColumnName,
            this.view.ConstantFilter
        );
    }

    get IsAttachmentsView(): boolean
    {
        return this.view.IsDocumentView;
    }

    isSingleCardView(view): boolean
    {
        return view.SingleCard || view.$$ParentView.SingleCard;
    }

    isHidden(view: IViewMetadata): boolean
    {
        if (!Utilities.IsNullOrUndefinedOrEmpty(view.HiddenProperty))
        {
            if (Utilities.IsNullOrUndefinedOrEmpty(view.SubPropertyType))
            {
                view.SubPropertyType = this.view.SubPropertyType;
            }
            return this.EvaluateBooleanOnView(view.HiddenProperty, view);
        }
        return false;
    }

    get disableViewAction(): boolean
    {
        if (this.$metadataViewService.IsGrid(this.view) && (!Utilities.IsNullOrUndefined(this.view.EditForm) || this.view.LazyLoadEditForm))
        {
            return false;
        }
        return true;
    }

    private EvaluateBooleanOnView(propString: string, view: IViewMetadata): boolean
    {
        let model = this.model;
        if (
            view.SubPropertyType &&
            this.datacontroller.OtherEntities && this.datacontroller.OtherEntities[view.SubPropertyType] &&
            !propString.includes("Parent")
        )
        {
            model = this.datacontroller.OtherEntities[view.SubPropertyType][0];
        }
        return this.$metadataService.EvaluateBoolean(propString, model, model);
    }

    private EvaluateBoolean(propString: string): boolean
    {
        return this.EvaluateBooleanOnView(propString, this.view);
    }

    @LogInvocation(LogLevel.DEBUG)
    editGridMultiple(initial: boolean, index: number = 0, selectedViewPath?: string): Promise<any>
    {
        const self = this;
        this.datacontroller["$$childFormIsEditing"] = true;
        self.$gridEventService.fireGridEditViewLoadingListener(true);
        const providerId = self.view["GridProperties"]["$providerId"];
        if (initial)
        {
            index = this.$multipleEditEntityService.CurrentEditIndex;
            if (!this.view.EditAction)
                this.$multipleEditEntityService.editNextProviders.push(providerId);
        }
        const entities = this.$multipleEditEntityService.EditableEntities;
        let nextEntity: any;
        if (!this.view.EditAction)
        {
            this.$multipleEditEntityService.ProviderId = providerId;
            this.$multipleEditEntityService.MultipleEditTotal = entities.length;
            nextEntity = entities[index];
        }
        else
            nextEntity =  this.$multipleEditEntityService.editActionEntity;

        return this.singleEdit(nextEntity, selectedViewPath)
            .then(() =>
            {
                self.$gridEventService.fireGridEditViewLoadingListener(false);
                if (!this.view.EditAction && entities.length === index + 1 && !self.$metadataViewService.IsProjectionGrid(self.view))
                    self.$metadataService.FireGridRefetch(nextEntity.$$TypeName);
            })
            .finally(() =>
            {
                self.$gridEventService.fireGridEditViewLoadingListener(false);
                self.$multipleEditEntityService.MultipleEditLoading = false;
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    private singleEdit(entity: any, selectedViewPath?: string): Promise<any>
    {
        if (!entity)
        {
            return Promise.resolve();
        }

        const id = ObjectHelpers.GetId(this.view["GridProperties"].IdProperty, entity, 0);
        if (this.$metadataViewService.IsProjectionGrid(this.view))
        {
            this.$multipleEditEntityService.setEditingFlag(entity);
            const rowTypeName = this.view.GridProperties["RowTypeName"];
            const idProperty = entity.SerializedRowState === "Added" && !Utilities.IsNullOrUndefinedOrZero(entity["$$AddedRowId"])
                ? "$$AddedRowId"
                : this.view.GridProperties["IdProperty"];
            const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
                this.datacontroller,
                () => this.singleEdit(entity),
                entity);

            if (this.view["$editInNewTab"])
            {
                return this.editInNewTab(entity, idProperty, id);
            }
            return this.datacontroller.findExistingOrGet(rowTypeName, idProperty, id, this.$repositoryService, this.datacontroller.ConfirmationQuestions)
                .then(
                    result =>
                    {
                        if (!result["$isEntityWrapper"])
                        {
                            const modelWrapper = new EntityWrapper(result, this.$metadataService);
                            result = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
                        }
                        this.view["$projectionGridEditId"] = id;
                        result.$$TypeName = rowTypeName;
                        if (!this.datacontroller.Entity)
                        {
                            this.datacontroller.Entity = result;
                            this.datacontroller.Type = rowTypeName;
                        }
                        else
                        {
                            this.datacontroller.Add(rowTypeName, result);
                        }

                        return this.editGrid(result, false, selectedViewPath);
                    },
                    rejectionHandler);
        }
        else if (this.serverPaging)
        {
            return this.$repositoryService.Get(
                {
                    Item1: entity.$$TypeName,
                    Item2: id,
                })
                .then(result =>
                {
                    return this.editGrid(result, false, selectedViewPath);
                });
        }
        else return this.editGrid(entity, false, selectedViewPath);
    }

    private editInNewTab(entity: any, idProperty: string, id: string): Promise<any>
    {
        const self = this;
        self.view["$projectionGridEditId"] = id;
        delete this.view["$editInNewTab"];

        const path = window.location.href;
        const parts = path.split("/");
        const start = parts.indexOf("apps") + 1;
        let route = parts.slice(start, parts.length).join("/");
        if (route.indexOf("?") > -1)
            route = route.substring(0, route.indexOf("?"));
        if (route.endsWith("/"))
            route = route.substring(0, route.length - 1);

        const gridView = <IBaseGridViewMetadata>this.view;
        const rowTypeNameSegments = gridView.GridProperties["RowTypeName"].split(".");
        const queryParams = {};
        if (this.queryParams.has("path"))
        {
            queryParams["path"] = this.queryParams.get("path").replace(/&/g, "%26");
        }
        queryParams["n"] = 1;

        if (!Utilities.IsNullOrUndefined(this.view["PropertyMetadataFilter"]))
        {
            queryParams["lazyLoadFormType"] = this.view["EditFormType"];
            queryParams["lazyLoadFormFilter"] = this.EvaluateLazyLoadEditFormFilter(entity);
        }
        queryParams["formType"] = rowTypeNameSegments[rowTypeNameSegments.length - 1];
        queryParams["editFormId"] = this.view["$projectionGridEditId"];
        queryParams["idProperty"] = idProperty;

        const url = this.$navigationService.getLinkToApp(route, queryParams) as string;
        const w = window.open(url, "_blank");
        window.focus();
        setTimeout(function ()
        {
            w.onbeforeunload = function ()
            {
                if ((<IGridViewMetadata>self.view).EditForm.RecalculateParentFormOnSave)
                {
                    return self.$repositoryService.RecalculateGridView({
                        Item1: self.datacontroller,
                        Item2: self.view["GridId"],
                    })
                        .then(
                            rdc =>
                            {
                                self.$metadataActionService.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc);
                                self.datacontroller.Merge(rdc);
                                self.$metadataService.FireGridReload(rdc.Type, true);
                            });
                }
            };
        }, 1000);
        return Promise.resolve();
    }

    private editGrid(entity: any, addMode: boolean, selectedViewPath?: string): Promise<any>
    {
        if (this.recalculating)
        {
            this.editAfterRecalculate = true;
            this.entityToEditAfterRecalculate = entity;
            this.editAfterRecalculateIsAddMode = addMode;
            this.gridLoading = true;
        }
        if (this.editAfterRecalculate)
        {
            return Promise.resolve();
        }
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error(`editGrid is not a supported function of non-grid views.`);
        }
        if (!addMode)
        {
            const editableGrid = this.$metadataViewService.IsCellEditGrid(this.view) || this.$metadataViewService.IsMetadataPanelGrid(this.view);
            if (!editableGrid)
                this.setupMultipleAddEdit(this.view);
        }

        const type = this.getGridTypeName();
        const editController = new RepositoryFormData(type, entity);
        editController.SetParentFormData(this.datacontroller, addMode);
        editController.SetEntityComparator(this.datacontroller.EntityComparator);

        this.editAfterRecalculateIsAddMode = false;
        this.entityToEditAfterRecalculate = undefined;

        return this.ExecuteEdit(editController, addMode, selectedViewPath);
    }

    private ExecuteEdit(editController: RepositoryFormData, addMode: boolean, selectedViewPath?: string): Promise<any>
    {
        const gridView = this.view;
        if (!this.$metadataViewService.IsGrid(gridView))
        {
            throw new Error(`editGrid is not a supported function of non-grid views.`);
        }

        if (gridView.EditAction)
        {
            return this.ExecuteEditAction(editController, addMode);
        }

        const entity = editController.Entity;
        if (gridView.LazyLoadEditForm && (!gridView.EditForm || gridView.PropertyMetadataFilter))
        {
            const filter = gridView.PropertyMetadataFilter && entity
                ? this.EvaluateLazyLoadEditFormFilter(entity)
                : null;
            return this.$formMetadataService.GetByType(gridView.EditFormType, filter)
                .then(r =>
                {
                    r["$LazyLoadFilter"] = gridView.PropertyMetadataFilter;
                    r["$LazyLoadFormType"] = gridView.EditFormType;
                    gridView.EditForm = r;

                    this.GoToEditForm(gridView.EditForm, editController, entity, addMode, selectedViewPath);
                });
        }

        if (gridView.EditForm)
        {
            this.GoToEditForm(gridView.EditForm, editController, entity, addMode, selectedViewPath);
        }

        return Promise.resolve();
    }

    private EvaluateLazyLoadEditFormFilter(entity: any): string
    {
        const constantFilter = this.$metadataService.EvaluateString(
            this.view["PropertyMetadataFilter"],
            entity.$$Parent,
            entity, null, true
        );
        let filter = this.$odataFilterFactory
            .createBuilderFor<IFormMetadata>()
            .constantFilter(constantFilter)
            .build();
        if (filter.startsWith("&"))
            filter = filter.substring(1);
        return filter;
    }

    private ExecuteEditAction(editController: RepositoryFormData, addMode: boolean): Promise<any>
    {
        const self = this;
        return this.$metadataActionService
            .ExecuteAction(editController.Entity, this.view.EditAction, editController)
            .then(x =>
            {
                if (x && x["$NextActionChain"])
                {
                    x["$NextActionChain"].then(x2 =>
                    {
                        resolveAction(x2);
                    });
                }
                else
                {
                    resolveAction(x);
                }
            })
            .finally(() =>
            {
                if (addMode)
                    self.$gridEventService.fireTableLoadingFinishedListeners(self.view["GridProperties"]["$providerId"]);
            });
        function resolveAction(x: any): void
        {
            if (addMode && MetadataActionHelpers.IsCancelObject(x))
                self.$metadataFormService.handleCancelAdded(editController.Entity, self.datacontroller, self.model, self.view);

            if (x && (self.view.ReloadOnSave || Utilities.IsNullOrUndefined(self.view.EditAction.ViewToShow) || (addMode && MetadataActionHelpers.IsCancelObject(x))))
            {
                self.clearCacheAndLoadGrid();
                self.fireTableListeners();
            }
        }
    }

    @LogInvocation(LogLevel.DEBUG)
    private GoToEditForm(
        formMetadata: IFormMetadata,
        editController: RepositoryFormData,
        entity: any,
        addMode: boolean,
        selectedViewPath?: string
    ): void
    {
        CacheHelperService.SetEditFormCacheCheckpointId((<IGridViewMetadata>this.view).GridProperties["$providerId"]);
        let index: number;
        if (!this.projectionOrServerGrid)
        {
            index = this.gridData.indexOf(entity);
            if (addMode) index = this.gridData.length - 1;
        }
        const params: goToFormBoundMethodParameter = {
            view: formMetadata,
            model: entity,
            datacontroller: editController,
            addMode: addMode,
            index: index,
            directSave: this.directSave,
            initialView: selectedViewPath,
        };
        const queryParams: Params = {};
        if (!addMode)
        {
            //Direct link, only support first level edit form for now
            if (this.$metadataViewService.IsProjectionGrid(this.view) && this.view["$projectionGridEditId"] && this.view.GridProperties["RowTypeName"] && this.$metadataFormService.initialForm && window.location.pathname.indexOf("/apps/processing") < 0)
            {
                const rowTypeNameSegments = this.view.GridProperties["RowTypeName"].split(".");
                queryParams.$filter = null;
                if (!Utilities.IsNullOrUndefined(this.view["PropertyMetadataFilter"]))
                {
                    queryParams.lazyLoadFormType = this.view["EditFormType"];
                    queryParams.lazyLoadFormFilter = this.EvaluateLazyLoadEditFormFilter(entity);
                }
                queryParams.formType = rowTypeNameSegments[rowTypeNameSegments.length - 1];
                queryParams.editFormId = this.view["$projectionGridEditId"];
                queryParams.idProperty = this.view.GridProperties["IdProperty"];
                params.projectionEdit = true;
            }
        }
        // queryParams.view = null;
        editController["$editFormQueryParams"] = queryParams;
        this.$metadataEventService.fireGoToFormListener(params);
    }

    private _onDeleteMethod: onDeleteBoundMethodSignature;

    onDeleteBind(): onDeleteBoundMethodSignature
    {
        if (!this._onDeleteMethod)
        {
            const self = this;
            this._onDeleteMethod = (t: onDeleteBoundMethodParameter) => self.onDeleteCombined(t.typename, t.model);
        }
        return this._onDeleteMethod;
    }

    onDeleteCombined(typename: string, model: any): Promise<any>
    {
        if (!Utilities.IsNullOrUndefined(this.onDelete))
            return this.onDelete({
                typename: typename,
                model: model,
            })
                .then(() => (this.loading = false));
    }

    getGridTypeName(): string
    {
        return this.$metadataViewService.GetGridTypeName(this.view);
    }

    private _preCommandFunctionBind: any;

    preCommandFunctionBind(): any
    {
        if (!this._preCommandFunctionBind)
        {
            const self = this;
            this._preCommandFunctionBind = (arg: RepositoryFunctionArgs) => self.preCommandFunction(arg.entity, arg.command);
        }
        return this._preCommandFunctionBind;
    }

    preCommandFunction(model: any, command: string): boolean
    {
        switch (command)
        {
            case "Add":
                return this.$metadataActionService.OpenNewTabIfNeeded(model, this.view.AddAction);
            case "Edit":
                return this.$metadataActionService.OpenNewTabIfNeeded(model, this.view.EditAction);
            default:
                return false;
        }
    }

    private _commandFunctionBind: any;

    commandFunctionBind(): any
    {
        if (!this._commandFunctionBind)
        {
            const self = this;
            this._commandFunctionBind = (arg: RepositoryFunctionArgs) => self.commandFunction(arg.entity, arg.command);
        }
        return this._commandFunctionBind;
    }

    commandFunction(entity: any, command: string): Promise<any>
    {
        const self = this;
        if (!self.$metadataViewService.IsGrid(self.view))
        {
            throw new Error(`commandFunction is not a supported function of non-grid views.`);
        }
        switch (command)
        {
            case "Add":
                self.create();
                break;
            case "Edit":
                return this.singleEdit(entity);
            case "EditMultiple":
                return self.editGridMultiple(true);
            case "Delete":
                return self.$gridDeleteService.AllowDeleteCheck(
                    entity,
                    self.datacontroller,
                    self.model,
                    self.view,
                    self.projectionOrServerGrid,
                    self.IsSubProperty,
                    self.gridData,
                    () => self.onDelete({
                            typename: self.$metadataViewService.GetGridTypeName(self.view),
                            model: entity,
                        }));
            case "Copy":
                self.setupMultipleAddEdit(self.view);
                self.copy(entity);
                break;
            default:
                break;
        }
        return Promise.resolve();
    }

    AllowDeleteCheckBind(arg: AllowDeleteFunctionArgs): void
    {
        this.$gridDeleteService.AllowDeleteCheck(arg.entity, this.datacontroller, this.model, this.view, this.projectionOrServerGrid, this.IsSubProperty, this.gridData, arg.deleteFunction);
    }

    get hasGridData(): boolean
    {
        return this.gridData && this.gridData.length > 0;
    }

    private prepareNewRow(newItem: IAugmentedWithParent): Promise<void>
    {
        if (!newItem.$$TypeName)
        {
            newItem.$$TypeName = this.getGridTypeName();
        }
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error("addToGrid is not a supported function of non-grid views.");
        }
        return this.setParent(newItem);
    }

    AddToGrid(newItem: IAugmentedWithParent, launchEdit: boolean = true): void
    {
        const parentResult = this.prepareNewRow(newItem);
        newItem["$pristine"] = true;
        if (!newItem["$isEntityWrapper"])
        {
            const modelWrapper = new EntityWrapper(newItem, this.$metadataService);
            newItem = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this.$metadataService));
        }

        const add = () =>
        {
            if (Utilities.IsNullOrUndefined(this.view.SubPropertyName))
            {
                this.loading = true;

                if (!this.serverPaging || this.projectionOrServerGrid)
                    this.AddToFormData(newItem);
                this.finishLoading();
            }
            else
            {
                if (this.$metadataViewService.IsProjectionGrid(this.view))
                    this.AddToFormData(newItem);
                else
                {
                    if ((<IGridViewMetadata>this.view).GridProperties.AddToTop)
                    {
                        this.gridData.unshift(newItem);
                    }
                    else
                    {
                        this.gridData.push(newItem);
                    }
                }
            }
            this.loadParentProperties(this.view, newItem, this.model);
            if ((this.$metadataViewService.IsReadOnlyGrid(this.view) || this.$metadataViewService.IsGridWithEditForm(this.view)) && launchEdit)
            {
                this.editGrid(newItem, true);
            }

            if (this.$metadataViewService.IsCellEditGrid(this.view))
            {
                if (this.recalculating)
                {
                    this.cellEditAfterRecalculate = true;
                    this.entityToEditAfterRecalculate = newItem;
                }
                else
                    this.fireCellEditOnAdd(newItem);
            }
            else
                this.fireTableListeners();

            this.$gridMetadataService.triggerAddRow();
        };
        if (parentResult)
            parentResult.then(add);
        else
            add();
    }

    private fireCellEditOnAdd(newItem: IAugmentedWithParent): void
    {
        const gridView = <IGridViewMetadata>this.view;
        if (gridView.EnableAutomaticEntry && !gridView.GridProperties["$providerId"])
        {
            this.fireTableListeners();
            gridView.GridProperties["$providerId"] = "metadataTable_" + Utilities.generateRandomGuid();
            this.$cellEditService.CellEditAfterAdd[gridView.GridProperties["$providerId"]] = true;
        }
        else
        {
            newItem["$addedCellEdit"] = true;
            this.fireTableListeners();
            this.$gridEventService.fireCellEditOnAddListener(gridView.GridProperties["$providerId"]);
        }
    }

    private AddToFormData(newItem: IAugmentedWithParent): void
    {
        const gridTypeName = this.getGridTypeName();
        if ((<IGridViewMetadata>this.view).GridProperties.AddToTop)
        {
            this.datacontroller.AddToTop(
                gridTypeName,
                newItem,
                this.$metadataViewService.createFilterString(this.model, this.view)
            );
        }
        else
        {
            this.datacontroller.Add(
                gridTypeName,
                newItem,
                this.$metadataViewService.createFilterString(this.model, this.view)
            );
        }
    }

    private setParent(newItem: IAugmentedWithParent): Promise<void>
    {
        let parent = this.model;
        if (!Utilities.IsNullOrUndefined(this.view.SubPropertyName))
        {
            if (this.view.SubPropertyType && this.datacontroller)
            {
                const filter = this.$metadataService.CreateLoadFilter(
                    parent,
                    this.view.ChildColumnName,
                    this.view.ParentColumnName,
                    this.view.ConstantFilter
                );
                return this.datacontroller
                    .GetOrFetch(this.$repositoryService, this.view.SubPropertyType, filter)
                    .then(x =>
                    {
                        parent = x[0];
                        if (!parent)
                        {
                            throw new Error(`Parent was null but ViewMetadata specified a SubProperty.`);
                        }
                        newItem.$$Parent = parent;
                        return;
                    });
            }
            else
            {
                const subVals = this.view.SubPropertyName.split(".");
                if (subVals.length > 1)
                {
                    const depProp = subVals.slice(0, subVals.length - 1)
                        .join(".");
                    parent = this.$metadataService.DependentModuleValue(depProp, null, this.model);
                    if (!parent)
                        parent = this.$metadataService.DependentModuleValue(depProp, null, this.datacontroller.Entity);
                }
            }
        }
        newItem.$$Parent = parent;
    }

    createFromCopy(entity: any): void//Promise<any>
    {
        this.copy(entity);
    }

    create(initial: boolean = true): Promise<any>
    {
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error(`create is not a supported function of non-grid views.`);
        }
        const self = this;
        const gridView = this.view;
        const editableGrid = this.$metadataViewService.IsCellEditGrid(this.view)
            || this.$metadataViewService.IsMetadataPanelGrid(this.view);

        if (this.view.GridProperties.MaxRows
            && this.gridData
            && this.view.GridProperties.MaxRows <= this.gridData.length)
        {
            let undeletedRecords = 0;
            this.gridData.forEach(r =>
            {
                if (r["SerializedRowState"] !== "Deleted") undeletedRecords++;
            });

            if (this.view.GridProperties.MaxRows <= undeletedRecords)
            {
                this.$commonDialogService.showAlertMessage(
                    this.view.Title + " cannot have more than " + this.view.GridProperties.MaxRows + " rows.",
                    "Alert");
                return;
            }
        }

        if (initial && !editableGrid)
        {
            this.setupMultipleAddEdit(gridView);
        }
        if (!this.view.AddAction)
        {
            const self = this;
            if (this.view.IsRepositoryType)
            {
                const rejectionHandler = this.$metadataActionService.GetVerificationHandler(
                    this.datacontroller,
                    () => self.create(),
                    this.model
                );
                const model = JSON.stringify({
                    ConfirmationQuestions: this.datacontroller.ConfirmationQuestions,
                });
                return this.$repositoryService
                    .GetNew(
                        {
                            Item1: this.getGridTypeName(),
                            Item2: model,
                        },
                        this.createFilterString()
                    )
                    .then(
                        r =>
                        {
                            r["SerializedRowState"] = "Added";
                            r["$added"] = true;
                            self.AddToGrid(r);
                            return r;
                        },
                        e =>
                        {
                            rejectionHandler(e);
                        }
                    )
                    .finally(() =>
                        self.$gridEventService.fireTableLoadingFinishedListeners(
                            gridView.GridProperties["$providerId"]
                        )
                    );
            }
            const r = <IAugmentedWithParent>{ $$Parent: this.model };
            r["SerializedRowState"] = "Added";
            r["$added"] = true;
            if (this.model && this.model["SerializedRowState"] && this.model["SerializedRowState"] === "Unchanged")
                this.model["SerializedRowState"] = "Modified";
            self.AddToGrid(r);
            return Promise.resolve(r);
        }
        let model = this.model;
        if (this.view.SubPropertyType && this.datacontroller.OtherEntities[this.view.SubPropertyType])
        {
            model = this.datacontroller.OtherEntities[this.view.SubPropertyType][0];
        }
        let refreshGrid = true;
        return this.$metadataActionService
            .ExecuteAction(model, this.view.AddAction, this.datacontroller)
            .then(r =>
            {
                if (MetadataActionHelpers.isIntentAction(this.view.AddAction))
                {
                    return;
                }
                if (!MetadataActionHelpers.hasCustomServicePath(this.view.AddAction))
                {
                    return;
                }
                else if (Array.isArray(r))
                {
                    r.forEach(el =>
                    {
                        el["$added"] = true;
                        this.AddToGrid(el, false);
                    });
                }
                else if (ObjectHelpers.isDataCollection(r))
                {
                    const editController = RepositoryFormData.create(r);
                    editController.SetEntityComparator(this.datacontroller.EntityComparator);
                    this.AddToGrid(r.Entity, false);
                    this.ExecuteEdit(editController, true);
                }
                else if (!!r && "canceledActionDialog" in r)
                {
                    if (r.canceledActionDialog !== true)
                    {
                        refreshGrid = false;
                        r["$added"] = true;
                        this.AddToGrid(r, true);
                    }
                }
                else
                {
                    //not set initially b/c add action, but action that returns this way flows into normal adding process
                    if (this.$multipleEditEntityService.maybeDelayAddSetup && !this.$metadataViewService.IsCellEditGrid(this.view))
                    {
                        this.$multipleEditEntityService.maybeDelayAddSetup = false;
                        this.$multipleEditEntityService.ProviderId = gridView.GridProperties["$providerId"];
                    }
                    refreshGrid = false;
                    r["$added"] = true;
                    this.AddToGrid(r, true);
                }
            })
            .finally(() =>
            {
                if (refreshGrid)
                    self.$gridEventService.fireTableLoadingFinishedListeners(gridView.GridProperties["$providerId"]);

                self._waitingToAdd = false;
                self._disableGridAdd = false;
                self.$multipleEditEntityService.maybeDelayAddSetup = false;
            });
    }

    initializeAddEditNew(): void
    {
        const self = this;
        if (!this.$metadataViewService.IsGrid(this.view))
            return;

        if (this.$multipleEditEntityService.hasEditNextListener(this.view.GridProperties["$providerId"]))
            this.$multipleEditEntityService.onEditNext.pipe(takeUntil(this.destructor$))
                .subscribe(arg =>
                {
                    self.$multipleEditEntityService.updateFilteredEditableEntities(<IGridViewMetadata>self.view);
                    self.editGridMultiple(false, arg.index, arg.selectedViewPath);
                });

        if (this.$multipleEditEntityService.hasAddNewListener(this.view.GridProperties["$providerId"]))
            this.$multipleEditEntityService.onAddNew.pipe(takeUntil(this.destructor$))
                .subscribe(() =>
                {
                    self.create(false)
                        .then(_ =>
                        {
                            if (self.$multipleEditEntityService.CopyProperties)
                                self.$multipleEditEntityService.DoCopy = true;
                        })
                        .finally(() => self.$multipleEditEntityService.MultipleEditLoading = false);
                });

        if (this.$multipleEditEntityService.hasAddAndCopyListener(this.view.GridProperties["$providerId"]))
            this.$multipleEditEntityService.onAddAndCopy.pipe(takeUntil(this.destructor$))
                .subscribe((entity) => self.createFromCopy(entity));
    }

    setupMultipleAddEdit(gridView: IGridViewMetadata): void
    {
        if (Utilities.IsNullOrUndefined(gridView.GridProperties["$providerId"]))
            gridView.GridProperties["$providerId"] = "metadataTable_" + GuidHelper.newGuid();
        if (this.$multipleEditEntityService.shouldInitiateMultipleAddEdit(this.view))
            this.$multipleEditEntityService.ProviderId = gridView.GridProperties["$providerId"];
        if (this.getAddPermission())
            this.$multipleEditEntityService.addNewProviders.push(gridView.GridProperties["$providerId"]);
        if (this.$metadataViewService.IsEditFormGrid(gridView) && this.$multipleEditEntityService.shouldInitiateMultipleAddEdit(this.view))
            this.$multipleEditEntityService.CopyProperties = gridView["CopyProperties"];
        if (this.$metadataViewService.CanCopyRecord(gridView, this.model))
            this.$multipleEditEntityService.addAndCopyProviders.push(gridView.GridProperties["$providerId"]);
    }

    private loadParentProperties(
        view: IViewMetadata,
        child: IAugmentedWithParent,
        parent: AugmentedModelObject | AugmentedModelObject[]
    ): void
    {
        if (!Utilities.IsNullOrUndefined(this.view.ParentPropertiesToTake))
        {
            view.ParentPropertiesToTake.forEach(p =>
            {
                if (p.includes("|"))
                {
                    const properties = p.split("|");
                    if (properties[1].startsWith("OtherEntities"))
                    {
                        //this should almost NEVER be used. Very rare edge case where a constant filter will not work
                        const pattern = /^OtherEntities\.([\w.]*)(?:\[(\d+)\])?\.?([\w.]*)?/;
                        const matches = pattern.exec(properties[1]);
                        const type = matches[1];
                        const index = matches[2];
                        const property = matches[3];
                        child[properties[0]] = this.datacontroller.OtherEntities[type][index][property];
                    }
                    else child[properties[0]] = parent[properties[1]];

                    this.recalcTakenProperty(child, properties[0]);
                }
                else
                {
                    child[p] = parent[p];
                    this.recalcTakenProperty(child, p);
                }
            });
        }
    }

    private recalcTakenProperty(child: IAugmentedWithParent, propName: string): void
    {
        if (!this.$metadataViewService.IsGrid(this.view))
            return;

        const metadata = (<IGridViewMetadata>this.view).GridProperties.ClassMetadata;
        const prop = metadata?.Properties?.find(p => p.Name === propName);
        if (!prop ||
            (Utilities.IsNullOrUndefined(prop.RecalculateOnChange) &&
             Utilities.IsNullOrUndefined(prop.RecalculateWithParent)))
            return;

        const recalc = <PropertyMetadata>{
            Name: propName,
            RecalculateOnChange: prop.RecalculateOnChange ?? [],
            RecalculateWithParent: prop.RecalculateWithParent,
            RecalculateDoNotSend: prop.RecalculateDoNotSend,
            RecalculateToIgnore: prop.RecalculateToIgnore
        };

        this.$metadataPropertyService.recalculateProperties(
            child["$$TypeName"] ?? metadata?.TypeName,
            recalc,
            child,
            child["$$Parent"],
            child[propName],
            undefined,
            false,
            false,
            this.datacontroller);
    }


    private copy(entity: any): Promise<any>
    {
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error(`copy is not a supported function of non-grid views.`);
        }
        const self = this;
        const handler = this.$metadataActionService.GetVerificationHandler(
            this.datacontroller,
            () => self.copy(entity),
            entity
        );


        if (this.$metadataViewService.IsProjectionGrid(this.view))
        {
            const id = ObjectHelpers.GetId(this.view["GridProperties"].IdProperty, entity, 0);
            const rowTypeName = this.view.GridProperties["RowTypeName"];
            const idProperty = this.view.GridProperties["IdProperty"];
            return self.datacontroller.findExistingOrGet(rowTypeName, idProperty, id, self.$repositoryService, this.datacontroller.ConfirmationQuestions)
                .then(result =>
                {
                    return self.doCopy(result);
                }, handler)
                .finally(() => self.$multipleEditEntityService.MultipleEditLoading = false);
        }
        else
            return self.doCopy(entity);
    }

    editViewsAreDifferentType(type: string, views: IViewMetadata[]): boolean
    {
        let ret = false;
        views.forEach(v =>
        {
            if (!this.$metadataViewService.IsCombinedView(v))
                if (!this.$metadataViewService.IsGrid(v) && v.ViewMetaData && v.ViewMetaData.TypeName !== type && v.ParentColumnName)
                    ret = true;

            if (this.$metadataViewService.IsCombinedView(v) && this.editViewsAreDifferentType(type, v.Views))
                ret = true;
        });
        return ret;
    }

    actionCallback(action: IMetadataAction, result: any): void
    {
        if (result && !Utilities.IsNullOrUndefined(action))
        {
            const isGrid = this.$metadataViewService.IsGrid(this.view);
            if (!Utilities.IsNullOrUndefined(action.ViewToShow) && action.ViewToShow.ReloadOnSave)
            {
                if (isGrid)
                {
                    this.clearCacheAndLoadGrid();
                }
            }
            else if (isGrid)
            {
                this.fireTableListeners();
            }
        }
    }

    doCopy(entity:any): Promise<any>
        {
            const self = this;
            const params = <RepositoryDataCollection>{
                Type: this.getGridTypeName(),
                Entity: entity,
                ConfirmationQuestions: this.datacontroller.ConfirmationQuestions,
            };
            const copyHandler = self.$metadataActionService.GetVerificationHandler(
                self.datacontroller,
                () =>
                    self.doCopy(params.Entity),
                params.Entity
            );

            if ((<IGridViewMetadata>self.view).GridProperties["CopyWithParent"])
            {
                let inParent = null;

                params.OptionalParentEntity = params.Entity["$$Parent"];
                params.OptionalParentType = params.OptionalParentEntity["$$TypeName"];

                inParent = self.$metadataPropertyService.FindInParent(params.OptionalParentEntity, params.Entity);

                if (inParent)
                {
                    params.Entity = undefined;
                    params.PropertyFromParent = inParent.Item1;
                    params.PropertyFromParentIndex = inParent.Item2;
                }
            }

            return self.$repositoryService.Copy(params)
                .then(newEntity =>
                {
                    self.datacontroller.ConfirmationQuestions = undefined;
                    newEntity["SerializedRowState"] = "Added";
                    newEntity["$added"] = true;
                    self.AddToGrid(newEntity);
                    if (self.datacontroller && self.editViewsAreDifferentType(params.Type, self.view["EditForm"].Views))
                        self.datacontroller["$CopySourceId"] = ObjectHelpers.GetId(self.view["GridProperties"].IdProperty, params.Entity, 0);
                }, copyHandler)
                .finally(() => self.$multipleEditEntityService.MultipleEditLoading = false);
        }

    subCallback(args: ModifierCommandArgs): void
    {
        if (Utilities.IsNullOrUndefined(this.modifierCallback)) return;
        this.modifierCallback.emit(args);
    }

    gridCallbackBind(arg: ModifierCommandArgs): void
    {
        this.gridCallback(arg.model, arg.propertyChanged, arg.propertyValue);
    }

    bindGridCallback(args: ModifierCommandArgs): void
    {
        this.gridCallback(args.model, args.propertyChanged, args.propertyValue);
    }

    gridCallback(model: any, propertyChanged: string, propertyValue: any): void
    {
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error(`gridCallback is not a supported function of non-grid views.`);
        }
        if ("SerializedRowState" in model && propertyChanged === "SerializedRowState" && propertyValue === "Deleted")
        {
            if (model.SerializedRowState === "Added")
                this.datacontroller.Remove(
                    this.view.GridProperties.ClassMetadata.TypeName,
                    model,
                    this.$metadataViewService.createFilterString(this.model, this.view)
                );
            else
                model.SerializedRowState = "Deleted";
        }
        if (propertyChanged === "FilterParameterChanged")
        {
            this.handleFilterChange(model);
        }
        if (propertyChanged === "RemovedFromGrid" && this.view.RecalculateOnDelete)
        {
            const self = this;
            self.$metadataEventService.fireRecalculateWorkingListeners(
                {
                    ShowLoading: false,
                    Recalculating: true,
                },
                true
            );
            this.$repositoryService
                .RecalculateGridView({
                    Item1: this.datacontroller,
                    Item2: self.view["GridId"],
                })
                .then(
                    rdc =>
                    {
                        self.$metadataActionService.ProcessAfterSave(rdc.AfterSaveContext, rdc.Entity, rdc);
                        this.datacontroller.Merge(rdc);
                        self.$metadataService.FireGridReload(rdc.Type, true);
                        self.$metadataEventService.fireRecalculateWorkingListeners(
                            {
                                ShowLoading: false,
                                Recalculating: false,
                            },
                            true
                        );
                    },
                    self.$metadataActionService.GetVerificationHandler(
                        self.datacontroller,
                        () => Promise.resolve(self.gridCallback(model, propertyChanged, propertyValue)),
                        self.model
                    )
                );
        }
    }

    handleFilterChange(model: any): void
    {
        const self = this;
        if (!this.$metadataViewService.IsGrid(this.view))
        {
            throw new Error(`handleFilterChange is not a supported function of non-grid views.`);
        }
        const type = this.view.GridProperties.ClassMetadata.TypeName;
        const filterMetadata = this.view.GridProperties.FilterMetadata;
        if (!this.projectionOrServerGrid)
        {
            this.datacontroller.Clear(type);
        }
        else
        {
            // These need to be cleared, or it will use cached data instead of fetching it
            this.$cacheHelperService.clearAllForType(type);
            this.$providerStorageService.GridData[this.view["GridProperties"]["$providerId"]] = undefined;
        }
        this.setGridFilter(model, filterMetadata);
        if (this.view.GridProperties.SaveOnFilterQuery)
        {
            const savecontroller = new RepositoryFormData(null, null);
            savecontroller.AddAll(type, this.gridData);
            this.gridLoading = true;
            this.$metadataActionService
                .ExecuteAction(this.gridData, <IMetadataAction>{}, savecontroller)
                .then(_x => self.clearCacheAndLoadGrid());
        }
        else
        {
            this.loadGrid();
        }
    }

    get isViewOnly(): boolean
    {
        return (
            this.viewOnly ||
            this.view.ViewOnly ||
            (!Utilities.IsNullOrUndefinedOrEmpty(this.view.ViewOnlyProperty) &&
                this.EvaluateBoolean(this.view.ViewOnlyProperty))
        ) && !this.view.ViewOnlyEnabled;
    }

    private setGridFilter(model: any, filterMetadata: ClassMetadata): void
    {
        const builder = this.$odataFilterFactory.createBuilder();
        for (const p in model)
        {
            if (!p.startsWith("$") && filterMetadata.Properties.filter(x => x.Name === p).length > 0)
            {
                builder.filterEQ(p, model[p]);
            }
        }
        this.view.ConstantFilter = builder.build()
            .replace("&$filter=", "");
    }

    getPermission(bool: boolean, boolProp: string): boolean
    {
        if (this.isViewOnly) return false;
        let model = this.model;
        if (this.view.SubPropertyType && this.datacontroller.OtherEntities[this.view.SubPropertyType])
        {
            model = this.datacontroller.OtherEntities[this.view.SubPropertyType][0];
        }
        return this.$metadataService.GetPermission(bool, boolProp, model);
    }

    showErrors(): void
    {
        const source = this.$metadataViewService.IsGrid(this.view) ? this.gridData : this.model;
        this.$metadataErrorService.showErrorAlertDialog(this.view, source);
    }

    get isViewOnlyGrid(): boolean
    {
        if (!this.$metadataViewService.IsViewOnlyGrid(this.view)) return false;
        return (
            this.viewOnly ||
            (!this.view.GridProperties.CanAdd &&
                !this.view.GridProperties.CanEdit &&
                !this.view.GridProperties.CanDelete)
        );
    }

    get projectionOrServerGrid(): boolean
    {
        // either will need to use OData select
        return (
            this.serverPaging || (this.$metadataViewService.IsProjectionGrid(this.view) && !this.view.SubPropertyName)
        );
    }

    get serverPaging(): boolean
    {
        return (
            this.$metadataViewService.IsGrid(this.view) &&
            this.view["GridProperties"].ServerPaging &&
            !this.view.SubPropertyName &&
            this.IsTable
        );
    }

    get IsSubProperty(): boolean
    {
        return (
            this.$metadataViewService.IsGrid(this.view) &&
            !Utilities.IsNullOrUndefined(this.view.SubPropertyName) &&
            (this.IsTable || this.IsMetadataPanelGrid)
        );
    }

    get collapseIcon(): string
    {
        return this.panelOpen ? "keyboard_arrow_up" : "keyboard_arrow_down";
    }

    get parentType(): string
    {
        if (this.view.SubPropertyType) return this.view.SubPropertyType;
        if (Utilities.IsNullOrUndefined(this.datacontroller) || Utilities.IsNullOrUndefined(this.datacontroller.Entity))
            return "";
        return this.datacontroller.Entity.$$TypeName;
    }

    get showMainHeader(): boolean
    {
        return Boolean(
            ((this.hasActions || this.isCollapsible || this.hasCollapsibleMenu()) &&
                (this.IsTable || this.IsCard || (this.IsCombinedView && !this.IsTabbedView) || this.IsAttachmentsView) && !this.IsMetadataPanelGrid) ||
            (this.$metadataViewService.IsCombinedView(this.view) && this.HasTitle() && !this.$metadataViewService.HideCombinedViewTitle(this.view) &&
                !this.view["$$ParentView"]?.Views.length) ||
            (this.hasActions && (this.IsCombinedView && this.IsTabbedView))
        );
    }

    toggleCollapsed(): void
    {
        this.panelOpen = !this.panelOpen;
    }

    get IsFooterView(): boolean
    {
        return this.footerView;
    }

    get showIntervalProgress(): boolean
    {
        return this.intervalRecalculating && !this.view.OnInterval.HideProgress;
    }

    setupReloadInterval(): void
    {
        let interval = this.view.OnInterval.RecalculateInterval * 1000;
        this.intervalRecalculating = true;
        if (interval < 1000)
            interval = 1000;
        this.doRecalc(interval);
    }

    doRecalc(interval: number): void
    {
        const self = this;
        this.onIntervalTimeout = setTimeout(() =>
        {
            self.runRecalcProperty(self.view.OnInterval).then(_r =>
            {
                if (self.intervalCanceled)
                    clearTimeout(self.onIntervalTimeout);
                else if (self.doReload)
                    self.doRecalc(interval);
                else
                {
                    self.loading = false;
                    self.intervalRecalculating = false;
                    self.$metadataActionService.ExecuteAction(null, self.view.OnInterval.FinalAction);
                }
            });
        }, interval);
    }

    get doReload(): boolean
    {
        if (this.view.OnInterval.RecalculateWhileProperty)
            return this.$metadataService.EvaluateBoolean(this.view.OnInterval.RecalculateWhileProperty, this.model["$$Parent"], this.model);
        return false;
    }

    savePanelSetting(open: boolean): void
    {
        if (this.view.Collapsible)
        {
            if(this.view.CollapsedProperty)
            {
                this.panelOpen = open;
                return;
            }

            const user = this.$securityService.principle.UserId;
            const typeName = this.$metadataViewService.GetTypeName(this.view);
            const title = this.view.Title;
            const path = window.location.href;

            const currentSetting: PanelSetting = {
                UserId: user,
                TypeName: typeName,
                Title: title,
                Path: path,
                Open: open
            };

            let settings: PanelSetting[] = this.$storageService.Get<PanelSetting[]>(this.CollapsiblePanel);

            if (!Utilities.IsNullOrUndefined(settings))
            {
                const match = settings.filter(x => x.Path === path && x.Title === title && x.TypeName === typeName && x.UserId === user);
                if (!Utilities.IsNullOrUndefined(match) && match.length > 0)
                {
                    match[0].Open = open;
                }
                else
                {
                    settings.push(currentSetting);
                }
            }
            else
            {
                settings = [];
                settings.push(currentSetting);
            }
            this.$storageService.Set(this.CollapsiblePanel, settings);
            this.$gridEventService.fireGridResizeListener();
        }
    }

    setPanelSetting(): void
    {
        if (this.view.Collapsible)
        {
            const settings: PanelSetting[] = this.$storageService.Get<PanelSetting[]>(this.CollapsiblePanel);

            if (!Utilities.IsNullOrUndefined(settings))
            {
                const match = settings.filter(x =>
                    x.Path === window.location.href
                    && x.Title === this.view.Title
                    && x.TypeName === this.$metadataViewService.GetTypeName(this.view)
                    && x.UserId === this.$securityService.principle.UserId
                );

                if (match.length > 0)
                {
                    this.panelOpen = match[0].Open;
                }
            }
        }
    }

    get hideToolbarBorder(): boolean
    {
        return !this.panelOpen;
    }

    get showDialogBusyIndicator(): boolean
    {
        return this.dialogIndex > 0 && (this.recalculating || this.$metadataActionService.actionRedirecting)
            && this.dialogIndex === this.$metadataActionService.activeDialogIndex && !this.intervalRecalculating;
    }

    trackByRow(index: number, row: number): number
    {
        return row;
    }
}
